import React, { useState, useEffect, useCallback } from 'react';
import { listCaregivers, createCaregiver, revokeCaregiver } from '../../lib/assessment-kiosk-admin-api';

export function FacilityCaregivers({ facilityId }) {
  const [caregivers, setCaregivers] = useState([]);
  const [newPin, setNewPin] = useState(null); // shown once after create
  const [form, setForm] = useState({ name: '', role: '', pin: '' });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await listCaregivers(facilityId);
      setCaregivers(res.caregivers || res || []);
    } catch (e) { setError(e.message); }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function onCreate() {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!/^\d{4,8}$/.test(form.pin)) { setError('PIN must be 4–8 digits.'); return; }
    setCreating(true); setError(null);
    try {
      await createCaregiver(facilityId, {
        name: form.name.trim(),
        role: form.role.trim(),
        pin: form.pin.trim(),
      });
      // The admin sets the PIN; the server stores only a bcrypt hash and never echoes it.
      // Surface the just-entered PIN once so the admin can hand it to the caregiver.
      setNewPin({ name: form.name.trim(), pin: form.pin.trim() });
      setForm({ name: '', role: '', pin: '' });
      await load();
    } catch (e) { setError(e.message); }
    finally { setCreating(false); }
  }

  async function onRevoke(cid) {
    if (!window.confirm('Revoke this caregiver? They will not be able to sign in.')) return;
    setError(null);
    try { await revokeCaregiver(facilityId, cid); await load(); }
    catch (e) { setError(e.message); }
  }

  return (
    <div className="kiosk-subsection">
      <h4>Caregivers</h4>
      {error && <div className="kiosk-admin-error">{error}</div>}

      {newPin && (
        <div className="kiosk-new-key">
          <strong>PIN for "{newPin.name}" — save it now, it will not be shown again:</strong>
          <code className="kiosk-key-box">{newPin.pin}</code>
          <button className="btn sm" onClick={() => navigator.clipboard?.writeText(newPin.pin)}>Copy</button>
          <button className="btn ghost sm" onClick={() => setNewPin(null)}>Dismiss</button>
        </div>
      )}

      <div className="kiosk-add-device">
        <input type="text" placeholder="Name" value={form.name}
          onChange={(e) => setField('name', e.target.value)} maxLength={100} />
        <input type="text" placeholder="Role (optional)" value={form.role}
          onChange={(e) => setField('role', e.target.value)} maxLength={60} />
        <input type="password" placeholder="PIN (4–8 digits)" value={form.pin}
          onChange={(e) => setField('pin', e.target.value)} maxLength={8} inputMode="numeric" />
        <button className="btn sm" disabled={creating} onClick={onCreate}>
          {creating ? 'Adding…' : 'Add caregiver'}
        </button>
      </div>

      <table className="kiosk-device-table">
        <thead><tr><th>Name</th><th>Role</th><th>Created</th><th></th></tr></thead>
        <tbody>
          {caregivers.length === 0 && (
            <tr><td colSpan={4} className="kiosk-empty">No caregivers yet.</td></tr>
          )}
          {caregivers.map((c) => (
            <tr key={c.id} className={c.revoked_at ? 'kiosk-revoked' : ''}>
              <td>{c.name}</td>
              <td>{c.role || '—'}</td>
              <td className="tabnum">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</td>
              <td>
                {c.revoked_at
                  ? <em>revoked</em>
                  : <button className="btn ghost sm" onClick={() => onRevoke(c.id)}>Revoke</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
